/* import __COLOCATED_TEMPLATE__ from './recipient-selector.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
// @ts-ignore
import { trackedReset } from 'tracked-toolbox';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency-decorators';
import { later } from '@ember/runloop';
import { taskFor } from 'ember-concurrency-ts';
import { schedule } from '@ember/runloop';
import { isValidEmail } from 'embercom/lib/email';
import UserSummary, { getParticipantChanges } from 'embercom/objects/inbox/user-summary';

import {
  DropdownNoMatchForType,
  DropdownSuggestion,
  DropdownUnselectableSuggestion,
} from 'embercom/objects/inbox/search/dropdown-items';
import type InboxSearchSuggestionsService from 'embercom/services/inbox-search-suggestions-service';
import type Session from 'embercom/services/session';
import type IntlService from 'embercom/services/intl';
import { useResource } from 'ember-resources';
import NavigableSelection from 'embercom/components/inbox2/common/navigable-selection-resource';
import {
  type ReplyChannel,
  replyChannelIsChat,
  replyChannelIsEmail,
  replyChannelIsSms,
  replyChannelIsWhatsapp,
  replyChannelIsSlack,
  replyChannelIsDiscord,
  replyChannelIsTelegram,
  replyChannelIsMultiParticipant,
} from 'embercom/objects/inbox/composer-pane';
// @ts-ignore
import { ref } from 'ember-ref-bucket';
import type Inbox2AssigneeSearch from 'embercom/services/inbox2-assignee-search';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type RouterService from '@ember/routing/router-service';
import type ClipboardService from 'embercom/services/clipboard-service';
import type NotificationsService from 'embercom/services/notifications-service';
import type { SenderEmailAddressSettings } from 'embercom/components/new-settings/workspace/brands/types/sender-email-address-settings';

interface Args {
  preventInput?: boolean;
  onRecipientChange: (recipients?: UserSummary[]) => void;
  recipients: UserSummary[];
  originalRecipients?: UserSummary[];
  conversationId?: number;
  setRecipientErrors: Function;
  isCreatingConversation?: boolean;
  isCreatingTicket?: boolean;
  replyChannel: ReplyChannel;
  selectNextInput?: Function;
  showRecipientSelector?: Function;
  onBlur?: Function;
  replyToData?: Array<UserSummary>;
  autofocus?: boolean;
  isSideConversation?: boolean;
}

interface Signature {
  Args: Args;
}

type SearchListItemType =
  | DropdownSuggestion
  | DropdownNoMatchForType
  | DropdownUnselectableSuggestion;

const KEYCODE_ENTER = 13;
const MAX_PARTICIPANTS = 10; // backend max 50, assuming higher than required for usage

export default class RecipientSelector extends Component<Signature> {
  @service declare inboxSearchSuggestionsService: InboxSearchSuggestionsService;
  @service declare inbox2AssigneeSearch: Inbox2AssigneeSearch;
  @service declare session: Session;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare clipboardService: ClipboardService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @service declare intercomEventService: any;
  @service declare inbox2BrandService: any;

  @tracked searchList: SearchListItemType[] = [];
  @tracked inputText = '';
  @tracked searchHasNoResults = false;
  @tracked selectedRecipientIndex?: number;
  @tracked isRemovingRecipient = false;
  @tracked isSelectingRecipient = false;
  @tracked _isFocused = false;
  @tracked canAccessContacts = false;
  @trackedReset('args.conversationId') hasFocused = false;

  @ref('recipient-input') declare recipientInput: HTMLElement;

  popover?: { hide: () => void; show: () => void };
  clipboard?: any;

  readonly keycodesThatCanPropagate = [KEYCODE_ENTER];

  readonly replyChannelIsChat = replyChannelIsChat;
  readonly replyChannelIsEmail = replyChannelIsEmail;
  readonly replyChannelIsSms = replyChannelIsSms;
  readonly replyChannelIsWhatsapp = replyChannelIsWhatsapp;
  readonly replyChannelIsSlack = replyChannelIsSlack;
  readonly replyChannelIsDiscord = replyChannelIsDiscord;
  readonly replyChannelIsTelegram = replyChannelIsTelegram;
  readonly replyChannelIsMultiParticipant = replyChannelIsMultiParticipant;
  readonly getParticipantChanges = getParticipantChanges;

  readonly list = useResource(this, NavigableSelection, () => ({
    items: this.searchList,
    equalityComparatorFn: this.equalityComparatorFn,
  }));

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    this.canAccessContacts = this.session.teammate.permissions.canAccessContacts;
    this.inbox2BrandService.loadBrandDataForUserSearch();
  }

  get inputLooksLikeEmail() {
    return this.inputText?.match(/.+\@.+\..+/);
  }

  get autofocus() {
    return (
      !this.shouldDisableAutofocus() &&
      (this.args.isCreatingConversation ||
        this.args.isCreatingTicket ||
        this.args.autofocus ||
        false)
    );
  }

  get isFocused() {
    return this._isFocused;
  }

  private recipientError(userSummary: UserSummary, index = 0): string | void {
    if (this.args.isSideConversation && this.isAdminEmail(userSummary.email)) {
      return this.intl.t('inbox.new-conversation.errors.recipient-is-admin');
    }

    if (userSummary && !userSummary.id && !isValidEmail(userSummary.email)) {
      return this.intl.t('inbox.new-conversation.errors.invalid-contact');
    }

    if (
      this.args.isCreatingConversation &&
      this.replyChannelIsChat(this.args.replyChannel) &&
      userSummary?.isNewUser
    ) {
      return this.intl.t('inbox.new-conversation.errors.recipient-has-email-only');
    }

    if (
      this.args.isCreatingConversation &&
      (this.replyChannelIsChat(this.args.replyChannel) ||
        this.replyChannelIsSlack(this.args.replyChannel) ||
        this.replyChannelIsSms(this.args.replyChannel) ||
        this.replyChannelIsDiscord(this.args.replyChannel) ||
        this.replyChannelIsTelegram(this.args.replyChannel)) &&
      index > 0
    ) {
      return this.intl.t('inbox.new-conversation.errors.group-conversation-email-only');
    }

    if (
      this.args.isCreatingConversation &&
      this.replyChannelIsEmail(this.args.replyChannel) &&
      (!userSummary?.email || !isValidEmail(userSummary.email))
    ) {
      return this.intl.t('inbox.new-conversation.errors.recipient-has-chat-only');
    }

    if (
      this.args.isCreatingConversation &&
      (this.replyChannelIsSms(this.args.replyChannel) ||
        this.replyChannelIsWhatsapp(this.args.replyChannel)) &&
      !userSummary?.phone
    ) {
      return this.replyChannelIsSms(this.args.replyChannel)
        ? this.intl.t('inbox.new-conversation.errors.recipient-missing-phone-number')
        : this.intl.t('inbox.new-conversation.errors.recipient-missing-whatsapp-phone-number');
    }

    if (
      this.args.isCreatingConversation &&
      this.replyChannelIsSlack(this.args.replyChannel) &&
      !userSummary?.userId?.startsWith('slack:') &&
      !userSummary?.slackEmail
    ) {
      return this.intl.t('inbox.new-conversation.errors.recipient-missing-slack-access');
    }

    return undefined;
  }

  get preventInput(): boolean {
    if (this.args.isCreatingConversation || this.args.isCreatingTicket) {
      if (
        (this.replyChannelIsChat(this.args.replyChannel) ||
          this.replyChannelIsSms(this.args.replyChannel) ||
          this.replyChannelIsWhatsapp(this.args.replyChannel) ||
          this.replyChannelIsSlack(this.args.replyChannel) ||
          this.replyChannelIsDiscord(this.args.replyChannel) ||
          this.replyChannelIsTelegram(this.args.replyChannel) ||
          this.args.isCreatingTicket) &&
        this.args.recipients.length > 0
      ) {
        // chat, sms, tickets, whatsapp, slack, discord only allow conversation creation with single recipient
        return true;
      } else if (
        this.replyChannelIsEmail(this.args.replyChannel) &&
        this.args.recipients.length >= MAX_PARTICIPANTS
      ) {
        return true;
      }

      return false;
    }

    return this.replyChannelIsMultiParticipant(this.args.replyChannel)
      ? false
      : this.args.recipients.length > 0;
  }

  get recipientsWithStatus() {
    let recipientStatuses: {
      recipient: UserSummary;
      recipientError: string | void;
      recipientStatus: string | void;
      replyTo: UserSummary | undefined;
      isNewParticipant: boolean;
      isRemovedParticipant: boolean;
    }[] = [];

    let addedParticipantsIds: string[] = [];
    let removedParticipantsList: UserSummary[] = [];
    if (!this.hasFocused) {
      let { addedParticipants, removedParticipants } = this.getParticipantChanges(
        this.args.originalRecipients || [],
        this.args.recipients,
      );
      addedParticipantsIds = addedParticipants?.map((participant) => participant.id);
      removedParticipantsList = removedParticipants;
    }

    this.args.recipients?.forEach((recipient, index) => {
      let replyTo = this.replyToForUser(recipient);
      let isNewParticipant = addedParticipantsIds.includes(recipient.id);
      recipientStatuses.push({
        recipient,
        recipientError: this.recipientError(recipient, index),
        recipientStatus: isNewParticipant
          ? this.intl.t('inbox.new-conversation.new-recipient-tooltip')
          : undefined,
        replyTo,
        isNewParticipant,
        isRemovedParticipant: false,
      });
    });
    if (this.args.setRecipientErrors) {
      this.args.setRecipientErrors(recipientStatuses);
    }

    removedParticipantsList.forEach((removedRecipient) => {
      recipientStatuses.push({
        recipient: removedRecipient,
        recipientError: undefined,
        recipientStatus: this.intl.t('inbox.new-conversation.removed-recipient-tooltip'),
        replyTo: undefined,
        isNewParticipant: false,
        isRemovedParticipant: true,
      });
    });

    return recipientStatuses;
  }

  replyToForUser(user: UserSummary) {
    return this.args.replyToData?.find((replyTo) => replyTo.id === user.id);
  }

  findByReplyTo(user: UserSummary) {
    return this.recipientsWithStatus.find((recipientData) => recipientData.replyTo === user)
      ?.recipient;
  }

  isAdminEmail(email: string | undefined) {
    if (!email) {
      return false;
    }

    return this.inbox2AssigneeSearch.findAdminByEmail(email) !== undefined;
  }

  shouldDisableAutofocus() {
    return (
      !this.args.autofocus &&
      this.replyChannelIsEmail(this.args.replyChannel) &&
      this.args.isCreatingConversation
    );
  }

  @action setPopover(popover: { hide: () => void; show: () => void }) {
    this.popover = popover;
  }

  @action
  addNewUser(email: string) {
    let user = new UserSummary('', email);
    this.addRecipient(user);
  }

  @action
  addRecipient(recipient: UserSummary) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'added',
      object: 'recipient',
      section: 'composer',
      is_new: !recipient.id,
      admin_id: this.session.teammate.id,
    });
    this.inputText = '';
    this.args.onRecipientChange([...this.args.recipients, recipient]);
    this.popover?.hide();
    this.searchList = [];
    this.searchHasNoResults = false;
    if (this.preventInput && this.args.selectNextInput) {
      this.args.selectNextInput();
    }
  }

  @action
  removeRecipient(recipient: UserSummary) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'removed',
      object: 'recipient',
      section: 'composer',
      admin_id: this.session.teammate.id,
    });

    this.isRemovingRecipient = true;
    if (this.args.recipients.includes(recipient)) {
      this.args.onRecipientChange(this.args.recipients.without(recipient));
    } else if (this.findByReplyTo(recipient)) {
      let user = this.findByReplyTo(recipient) as UserSummary;
      this.args.onRecipientChange(this.args.recipients.without(user));
    } else {
      this.args.onRecipientChange(this.args.recipients);
    }
    this.focusInput();
    this.popover?.hide();
  }

  @action
  onInput(e: { target: { value: string } }) {
    let input = e.target.value;
    if (input) {
      this.deselectRecipient();
      this.popover?.show();
    }

    let previousInputText = this.inputText;
    this.inputText = input;
    let mightHaveMoreResults = !this.searchHasNoResults || !input.startsWith(previousInputText);
    // If the user cannot access contacts, we must still try to match an exact email
    if (mightHaveMoreResults || !this.canAccessContacts) {
      taskFor(this.searchUsers).perform(input);
    }
  }

  @task({ restartable: true })
  *searchUsers(query: string) {
    this.searchList = [];
    let selectedUsers = this.args.recipients;
    let users: UserSummary[] = [];

    users = (yield this.inboxSearchSuggestionsService.getUserSuggestions(
      query,
      8,
      selectedUsers,
      'contains',
      this.replyChannelIsEmail(this.args.replyChannel) ? 'email' : 'name',
    )) as UserSummary[];

    // We have added this check since we might have some users with invalid emails containing the search param
    // Trying to send emails to those users fail in the backend so we want to prevent the selection in the frontend
    if (this.replyChannelIsEmail(this.args.replyChannel)) {
      users = users.filter((user) => {
        return !user.email || isValidEmail(user.email);
      });
    }
    if (this.replyChannelIsSlack(this.args.replyChannel)) {
      users = users.filter((user) => user.userId?.startsWith('slack:') || user.slackEmail);
    }

    let searchList: SearchListItemType[];

    if (users.length > 0) {
      this.searchHasNoResults = false;

      let selectedUsersIds = selectedUsers.map((user) => user.id);
      let unselectedUsers = users.filter((user) => !selectedUsersIds.includes(user.id));
      if (this.replyChannelIsEmail(this.args.replyChannel)) {
        unselectedUsers = unselectedUsers.sortBy('name');
      }

      // Create lookup maps for brand information
      let senderEmailAddressSettings =
        this.inbox2BrandService.senderEmailAddressSettings.value || [];
      let brands = this.inbox2BrandService.brands.value || [];

      // Lookup map from email to SenderEmailAddressSettings
      let senderEmailToSettingsMap = new Map<string, SenderEmailAddressSettings>(
        senderEmailAddressSettings
          .filter((setting: any) => setting.email)
          .map((setting: any) => [setting.email, setting]),
      );

      // Lookup map from brandId to brandName
      let brandIdToBrandNameMap = new Map(
        brands
          .filter((brand: any) => brand?.id && brand?.name)
          .map((brand: any) => [brand.id.toString(), brand.name]),
      );

      searchList = unselectedUsers.map((user) => {
        let brandName: string | undefined;

        // Lookup brand information
        if (senderEmailToSettingsMap && brandIdToBrandNameMap && user.email) {
          let brandId = senderEmailToSettingsMap.get(user.email)?.brandId;
          brandName = brandId
            ? (brandIdToBrandNameMap.get(brandId.toString()) as string | undefined)
            : undefined;
        }

        if (this.replyChannelIsEmail(this.args.replyChannel) && !user.email) {
          let suggestion = new DropdownUnselectableSuggestion(user) as any;
          if (brandName) {
            suggestion.brandName = brandName;
          }
          return suggestion;
        }
        let suggestion = new DropdownSuggestion(user, () => this.addRecipient(user)) as any;
        if (brandName) {
          suggestion.brandName = brandName;
        }
        return suggestion;
      });
    } else {
      this.searchHasNoResults = true;
      searchList = [new DropdownNoMatchForType(() => this.addNewUser(this.inputText))];
    }

    this.searchList = searchList;
  }

  @action async onPopoverHide() {
    // Make sure we wait for any searches that are running to finish.
    await taskFor(this.searchUsers).last;

    // If we have an input at this point, try to create a recipient with it
    let input = this.inputText?.trim() ?? '';
    let itemMatchingInput = this.searchList.find(
      (item: DropdownSuggestion) => item.suggestion?.email === input.toLocaleLowerCase(),
    );

    if (itemMatchingInput) {
      this.addRecipient((itemMatchingInput as DropdownSuggestion).suggestion);
    } else if (input) {
      this.addNewUser(this.inputText);
    }
  }

  @action equalityComparatorFn(a: any, b: any) {
    return a.suggestion?.id === b.suggestion?.id;
  }

  @action handleBackspace() {
    if (this.inputText) {
      return;
    }
    if (this.selectedRecipientIndex === undefined) {
      this.selectedRecipientIndex = this.args.recipients.length - 1;
    } else {
      this.removeRecipient(this.args.recipients[this.selectedRecipientIndex]);
      this.selectedRecipientIndex = undefined;
    }
  }

  @action previousRecipient() {
    if (this.selectedRecipientIndex !== undefined && this.selectedRecipientIndex > 0) {
      this.selectedRecipientIndex--;
    }
  }

  @action nextRecipient() {
    if (
      this.selectedRecipientIndex !== undefined &&
      this.selectedRecipientIndex < this.args.recipients.length
    ) {
      this.selectedRecipientIndex === this.args.recipients.length - 1
        ? this.deselectRecipient()
        : this.selectedRecipientIndex++;
    }
  }

  @action deselectRecipient() {
    this.selectedRecipientIndex = undefined;
  }

  @action focusInput() {
    this.args.showRecipientSelector && this.args.showRecipientSelector();
    schedule('afterRender', this, () => {
      this.recipientInput?.focus();
      this._isFocused = true;
    });
  }

  @action onFocus() {
    this.hasFocused = true;
    this._isFocused = true;
  }

  @action onBlur() {
    this._isFocused = false;

    if (this.args.onBlur) {
      if (this.isRemovingRecipient || this.isSelectingRecipient) {
        this.focusInput();
      } else {
        this.args.onBlur && this.args.onBlur();
      }
    }
    this.isSelectingRecipient = false;
    this.isRemovingRecipient = false;

    this.onPopoverHide();
  }

  @action onSuggestionClick(item: SearchListItemType, event: MouseEvent) {
    let isUserIdTooltip =
      event.target instanceof Element && event.target.closest('[data-is-user-id-tooltip]') !== null;
    if (isUserIdTooltip) {
      event.preventDefault();
      return;
    }

    this.isSelectingRecipient = true;
    item.onClickAction();
    later(this, this.focusInput, 20);
  }

  @action openUserProfile(workspaceId: string, userId: string) {
    let url = this.router.urlFor('apps.app.users.user', workspaceId, userId);
    safeWindowOpen(url, '_blank');
  }

  @action copyUserId(userId: string) {
    if (!this.clipboard) {
      this.clipboard = this.clipboardService.createClipboard(
        '#copy-user-id-button',
        userId,
        () => {
          this.notificationsService.notifyConfirmation(
            this.intl.t('inbox.new-conversation.recipient-selector-tooltip.user-id-copy-success'),
          );
        },
        () => {
          this.notificationsService.notifyWarning(
            this.intl.t('inbox.new-conversation.recipient-selector-tooltip.user-id-copy-error'),
          );
        },
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::NewConversation::RecipientSelector': typeof RecipientSelector;
    'inbox2/new-conversation/recipient-selector': typeof RecipientSelector;
  }
}
